// Day arithmetic for the Amazon overview, on UTC days so a timeline never shifts by one
// across a clock change.
const DAY_MS = 86_400_000;

export const day = (iso: string): Date =>
  new Date(`${iso.slice(0, 10)}T00:00:00Z`);

export const today = (): Date => day(new Date().toISOString());

export const addDays = (d: Date, n: number): Date =>
  new Date(d.getTime() + Math.round(n) * DAY_MS);

/** The same day ``n`` months on, or that month's last day where it is shorter
 * (amazon_overview._months_after). */
export const addMonths = (d: Date, n: number): Date => {
  const target = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + n, 1));
  const last = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0));
  return new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth(), Math.min(d.getUTCDate(), last.getUTCDate())));
};

export const daysBetween = (a: Date, b: Date): number =>
  Math.round((b.getTime() - a.getTime()) / DAY_MS);

/** England and Wales's bank holidays, as the backend serves them with the buildings
 * (working_days.public_holidays): the one calendar, so the page and the agents count the
 * same Business Days. Set before any building is read; one-off holidays come with it. */
let calendar: { years: Set<number>; days: Set<number> } | null = null;

export const setBankHolidays = (served: { years: number[]; dates: string[] }): void => {
  calendar = {
    years: new Set(served.years),
    days: new Set(served.dates.map((d) => day(d).getTime())),
  };
};

/** In a year the calendar does not cover, no day is a bank holiday, so Business Days
 * there count weekdays only: the building whose dates reach that year says so
 * (``uncountedYears``), and the rest of the page is unaffected. */
const isBankHoliday = (d: Date): boolean => {
  if (!calendar) throw new Error("The bank holidays are read before any building");
  return calendar.days.has(d.getTime());
};

/** How far either side of a date a Business Day count from it can reach: design
 * approval's 104 Business Days are about 150 calendar days. */
const REACH_DAYS = 200;

/** The years the Business Day counts around ``dates`` reach that the served calendar does
 * not cover, in order: there those counts are weekdays only, so the page flags them. */
export const uncountedYears = (dates: (Date | null | undefined)[]): number[] => {
  if (!calendar) return [];
  const covered = calendar.years;
  const years = new Set<number>();
  for (const d of dates) {
    if (!d || Number.isNaN(d.getTime())) continue;
    for (const x of [addDays(d, -REACH_DAYS), d, addDays(d, REACH_DAYS)]) {
      if (!covered.has(x.getUTCFullYear())) years.add(x.getUTCFullYear());
    }
  }
  return [...years].sort((a, b) => a - b);
};

/** The flag a building carries when its dates reach a year with no bank holidays served. */
export const uncountedText = (years: number[]): string =>
  `Cannot count business days for ${years.join(", ")}: no bank holidays are known for ${
    years.length > 1 ? "those years" : "that year"
  }, so design approval there counts weekdays only`;

/** A Business Day as the MSPPA defines one: a weekday that is not a bank holiday in
 * England and Wales. */
export const isBusinessDay = (d: Date): boolean =>
  d.getUTCDay() !== 0 && d.getUTCDay() !== 6 && !isBankHoliday(d);

/** ``n`` Business Days after ``d``; a negative ``n`` counts back. */
export const addBusinessDays = (d: Date, n: number): Date => {
  let x = d;
  let left = Math.abs(n);
  const step = n < 0 ? -1 : 1;
  while (left > 0) {
    x = addDays(x, step);
    if (isBusinessDay(x)) left -= 1;
  }
  return x;
};

export const latest = (...dates: (Date | null | undefined)[]): Date =>
  new Date(
    Math.max(...dates.filter((d): d is Date => !!d).map((d) => d.getTime())),
  );

export const formatDate = (d: Date | null): string =>
  d
    ? d
        .toLocaleDateString("en-GB", {
          day: "numeric",
          month: "short",
          year: "numeric",
          timeZone: "UTC",
        })
        .replace("Sept", "Sep")
    : "-";

/** "04/12/26". */
export const formatNumeric = (d: Date): string =>
  d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    timeZone: "UTC",
  });

/** "4 Dec", the year left off. */
export const formatShort = (d: Date): string =>
  formatDate(d).replace(/ \d{4}$/, "");
